import {
  Box,
  Button,
  Checkbox,
  FormControlLabel,
  MenuItem,
  TextField,
  Typography,
} from '@mui/material';
import React, { useState } from 'react';
import { mindFetch } from '../api';
import { API_BASE } from '../config';

const ProfileSetup = ({
  profile,
  onProfileUpdated,
  isEditing,
  onSignOut,
  onCancel,
}) => {
  const API_URL = `${API_BASE}/user`;

  const [firstName, setFirstName] = useState(profile?.first_name || '');
  const [lastName, setLastName] = useState(profile?.last_name || '');
  const [roleType, setRoleType] = useState(profile?.role_type || '');
  const [office, setOffice] = useState(profile?.office || '');
  const [team, setTeam] = useState(profile?.team || '');
  const [aiAcknowledgment, setAiAcknowledgment] = useState(
    profile?.ai_acknowledgment || false,
  );
  const [loading, setLoading] = useState(false);

  const handleSubmit = async (event) => {
    event.preventDefault();
    if (loading) return; // Prevent multiple submissions

    // Ensure the checkbox is checked during registration
    if (!isEditing && !aiAcknowledgment) {
      alert('You must acknowledge the use of AI to proceed.');
      return;
    }

    setLoading(true);

    const profileData = {
      first_name: firstName,
      last_name: lastName,
      role_type: roleType,
      office: office,
      team: team,
      ai_acknowledgment: aiAcknowledgment,
    };

    //console.log("📤 Sending Profile Data:", profileData);

    try {
      const response = await mindFetch(API_URL, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(profileData),
      });

      if (!response.ok) {
        const errorText = await response.text();
        throw new Error(`Error updating profile: ${errorText}`);
      }

      const data = await response.json();
      //console.log("✅ Profile Updated Successfully:", data);

      // Fetch updated user data
      //console.log("🟢 Fetching updated user data...");

      // Notify App.js that the profile is now complete
      //console.log("🟢 Calling onProfileUpdated...");
      await onProfileUpdated();
    } catch (error) {
      console.error('🚨 Error updating profile:', error);
    } finally {
      setLoading(false);
    }
  };

  return (
    <Box
      sx={{
        minHeight: '100vh',
        minWidth: '100vw', // Ensures the background covers the full viewport width
        width: '100vw', // Ensures the background covers the full viewport width
        backgroundColor: '#f5f5f5', // Light grey background
        display: 'flex',
        flexDirection: 'column',
        margin: 0, // Ensure no margin
        padding: 0, // Ensure no padding
      }}
    >
      {/* Full-Width White Header */}
      <Box
        sx={{
          backgroundColor: 'white',
          width: '100vw', // Expands to the full viewport width
          height: '70px',
          display: 'flex',
          alignItems: 'center',
          paddingLeft: '24px',
          boxShadow: '0px 2px 4px rgba(0, 0, 0, 0.1)',
          margin: 0, // Ensure no margin
        }}
      >
        <img
          src='/factor_mind_logo.png'
          alt='Factor Mind'
          style={{ width: '200px', height: 'auto' }}
        />
      </Box>

      {/* Centered Profile Setup Form */}
      <Box
        sx={{
          flexGrow: 1,
          display: 'flex',
          justifyContent: 'center',
          alignItems: 'center',
          width: '100%', // Ensures the form container spans the full width
          padding: '24px', // Adds some padding around the form
          margin: 0, // Ensure no margin
        }}
      >
        <Box
          sx={{
            backgroundColor: 'white',
            padding: '32px',
            borderRadius: '8px',
            boxShadow: '0px 4px 10px rgba(0, 0, 0, 0.1)',
            maxWidth: '500px',
            width: '100%', // Ensures the form takes up the full width of its container
            textAlign: 'center',
          }}
        >
          <Typography variant='h5' gutterBottom>
            {isEditing ? 'Edit Your Profile' : 'Complete Your Profile'}
          </Typography>

          <form onSubmit={handleSubmit}>
            <TextField
              fullWidth
              label='First Name'
              variant='outlined'
              value={firstName}
              onChange={(e) => setFirstName(e.target.value)}
              required
              sx={{ mb: 2 }}
            />
            <TextField
              fullWidth
              label='Last Name'
              variant='outlined'
              value={lastName}
              onChange={(e) => setLastName(e.target.value)}
              required
              sx={{ mb: 2 }}
            />

            <TextField
              select
              fullWidth
              label='Role'
              variant='outlined'
              value={roleType}
              onChange={(e) => setRoleType(e.target.value)}
              required
              sx={{ mb: 2 }}
            >
              <MenuItem value=''>Select Role</MenuItem>
              <MenuItem value='Analyst'>Analyst</MenuItem>
              <MenuItem value='Associate'>Associate</MenuItem>
              <MenuItem value='Director'>Director</MenuItem>
              <MenuItem value='Partner'>Partner</MenuItem>
              <MenuItem value='Other'>Other</MenuItem>
            </TextField>

            <TextField
              select
              fullWidth
              label='Office'
              variant='outlined'
              value={office}
              onChange={(e) => setOffice(e.target.value)}
              required
              sx={{ mb: 2 }}
            >
              <MenuItem value=''>Select Office</MenuItem>
              <MenuItem value='DFW'>DFW</MenuItem>
              <MenuItem value='Houston'>Houston</MenuItem>
              <MenuItem value='Central Texas'>Central Texas</MenuItem>
              <MenuItem value='Phoenix'>Phoenix</MenuItem>
            </TextField>

            <TextField
              select
              fullWidth
              label='Team'
              variant='outlined'
              value={team}
              onChange={(e) => setTeam(e.target.value)}
              required
              sx={{ mb: 2 }}
            >
              <MenuItem value=''>Select Team</MenuItem>
              <MenuItem value='Multi-Family'>Multi-Family</MenuItem>
              <MenuItem value='Single-Family'>Single-Family</MenuItem>
              <MenuItem value='Industrial'>Industrial</MenuItem>
              <MenuItem value='None'>None</MenuItem>
            </TextField>

            {!isEditing && (
              <>
                <Typography
                  variant='body2'
                  color='textSecondary'
                  sx={{ mb: 2, fontStyle: 'italic' }}
                >
                  By interacting with this AI-enabled platform, you acknowledge
                  that your information will be recorded to enhance our
                  services. The company is not liable for any AI-generated
                  recommendations or responses.
                </Typography>
                <FormControlLabel
                  control={
                    <Checkbox
                      checked={aiAcknowledgment}
                      onChange={(e) => setAiAcknowledgment(e.target.checked)}
                      color='primary'
                      required // Makes the checkbox mandatory
                    />
                  }
                  label='I understand that AI-generated responses may not always be accurate, and my interactions will be recorded to improve the platform.'
                  sx={{ mb: 2 }}
                />
              </>
            )}

            <Button
              type='submit'
              variant='contained'
              color='primary'
              fullWidth
              sx={{ mt: 2 }}
              disabled={loading}
            >
              {loading ? 'Saving...' : 'Save Profile'}
            </Button>
          </form>

          {/* Add Cancel and Sign Out Buttons for Editing Users */}
          {isEditing && (
            <Box sx={{ display: 'flex', gap: 2, mt: 2 }}>
              <Button
                variant='contained'
                color='secondary'
                fullWidth
                onClick={onCancel} // Handle cancel action
                sx={{
                  backgroundColor: '#9e9e9e',
                  color: 'white',
                  '&:hover': { backgroundColor: '#757575' },
                }}
              >
                Cancel
              </Button>
              <Button
                variant='contained'
                color='error'
                fullWidth
                onClick={onSignOut} // Handle sign-out action
              >
                Sign Out
              </Button>
            </Box>
          )}
        </Box>
      </Box>
    </Box>
  );
};

export default ProfileSetup;
